import './App.css';
import { useState, useMemo, useEffect } from 'react';

function App() {
  // Mot de passe pour accéder au site pendant la phase de construction (à modifier si besoin)
  const PREVIEW_PASSWORD = 'autodiag';

  // Gate "site en construction"
  const [unlocked, setUnlocked] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [unlockInput, setUnlockInput] = useState('');
  const [vehicle, setVehicle] = useState({
    plate: '',
    brand: '',
    model: '',
    year: '',
    fuel: 'essence',
  });

  const [services, setServices] = useState({
    keyCopy: false,
    ethanol: false,
    diagnostic: false,
    urgent: false,
  });

  const [customer, setCustomer] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    city: '',
    notes: '',
  });

  const handleVehicleChange = (e) => {
    const { name, value } = e.target;
    setVehicle((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (e) => {
    const { name, checked } = e.target;
    setServices((prev) => ({ ...prev, [name]: checked }));
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setCustomer((prev) => ({ ...prev, [name]: value }));
  };

  const [currentPage, setCurrentPage] = useState('accueil');
  const [navOpen, setNavOpen] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('dark');
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);

  const priceDetails = useMemo(() => {
    // Barème d'exemple à adapter à tes vrais tarifs
    const isRecent = Number(vehicle.year) >= 2015;
    const isPremiumBrand = ['BMW', 'AUDI', 'MERCEDES', 'PORSCHE', 'VOLKSWAGEN'].includes(
      vehicle.brand.trim().toUpperCase()
    );

    let baseKey = 80; // clé standard
    if (isRecent) baseKey += 40; // électronique récente
    if (isPremiumBrand) baseKey += 40;

    let baseEthanol = 450; // reprogrammation éthanol moyenne
    if (isPremiumBrand) baseEthanol += 100;

    let baseDiag = 60; // diagnostic standard

    const urgentMultiplier = services.urgent ? 1.2 : 1;

    const lines = [];

    if (services.keyCopy) {
      const price = Math.round(baseKey * urgentMultiplier);
      lines.push({ label: 'Reproduction de clé de voiture', price });
    }
    if (services.ethanol) {
      const price = Math.round(baseEthanol * urgentMultiplier);
      lines.push({ label: 'Reprogrammation moteur à l’éthanol (E85)', price });
    }
    if (services.diagnostic) {
      const price = Math.round(baseDiag * urgentMultiplier);
      lines.push({ label: 'Diagnostic électronique complet', price });
    }

    const total = lines.reduce((sum, line) => sum + line.price, 0);

    return { lines, total };
  }, [services, vehicle.brand, vehicle.year]);

  useEffect(() => {
    // Gate: check localStorage
    try {
      if (window.localStorage.getItem('universdiag_site_unlocked') === '1') {
        setUnlocked(true);
      }
    } catch (e) {
      // ignore
    }

    // Gate: check URL param ?preview=PASSWORD
    try {
      const usp = new URLSearchParams(window.location.search);
      const p = usp.get('preview');
      if (p && p === PREVIEW_PASSWORD) {
        setUnlocked(true);
        window.localStorage.setItem('universdiag_site_unlocked', '1');
      }
    } catch (e) {
      // ignore
    }

    try {
      const stored = window.localStorage.getItem('universdiag_theme');
      const tourDone = window.localStorage.getItem('universdiag_tour_done') === '1';
      if (stored === 'light' || stored === 'dark') {
        setTheme(stored);
        setSelectedTheme(stored);
        if (!tourDone) {
          setShowTour(true);
          setTourStep(0);
        }
      } else {
        setShowThemeModal(true);
      }
    } catch (e) {
      setShowThemeModal(true);
    }
  }, []);

  // Bloque le scroll de la page quand un overlay important est affiché (thème ou tour)
  useEffect(() => {
    const modalActive = showThemeModal || (showTour && currentPage === 'accueil');
    if (modalActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [showThemeModal, showTour, currentPage]);

  // Centre la vue sur la bonne zone pendant le tour guidé
  useEffect(() => {
    if (!showTour || currentPage !== 'accueil') return;
    let top = 0;
    if (tourStep === 0) {
      top = 70; // plaque
    } else if (tourStep === 1) {
      top = 170; // prestations
    } else if (tourStep === 2) {
      top = 240; // bouton CTA
    } else if (tourStep === 3) {
      top = 0; // bouton thème dans le header
    }
    window.scrollTo({ top, behavior: 'smooth' });
  }, [showTour, tourStep, currentPage]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Pour l’instant, on se contente de logguer les infos.
    // Tu pourras plus tard envoyer ces données vers un backend ou un outil (Google Sheet, CRM, etc.).
    console.log({ vehicle, services, customer, priceDetails });
    alert('Demande enregistrée côté navigateur (voir console). À connecter à un backend plus tard.');
  };

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem('universdiag_theme', next);
      } catch (e) {
        // ignore
      }
      return next;
    });
  };

  const renderAccueil = () => (
    <>
      <section className="intro-card">
        <h2>Atelier de reprogrammation automobile à Toulouse</h2>
        <p>
          Gardez votre véhicule en parfait état de fonctionnement avec L&apos;Univers Diag dans la
          Haute-Garonne.
        </p>
        <p>
          L&apos;Univers Diag à Toulouse est un atelier spécialisé dans la mécanique et l&apos;ingénierie
          automobile. Nous intervenons en urgence pour la reproduction des clés, la reprogrammation du
          moteur à l&apos;éthanol et le diagnostic électronique.
        </p>
        <p>
          Nos prestations sont destinées aux particuliers, aux concessionnaires et aux garages
          automobiles. Profite de nos solutions optimales pour reprendre la route en toute sérénité.
        </p>
        <div className="form-row">
          <label>
            Plaque d&apos;immatriculation
            <input
              type="text"
              name="plate"
              value={vehicle.plate}
              onChange={handleVehicleChange}
              placeholder="AB-123-CD"
              className={`plate-input ${
                showTour && tourStep === 0 && currentPage === 'accueil' ? 'tour-highlight' : ''
              }`}
            />
          </label>
        </div>
        <div
          className={`form-row quick-services ${
            showTour && tourStep === 1 && currentPage === 'accueil' ? 'tour-highlight' : ''
          }`}
        >
          <span className="quick-services-label">Prestations souhaitées</span>
          <div className="checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="keyCopy"
                checked={services.keyCopy}
                onChange={handleServiceChange}
              />
              <span>Reproduction de clé de voiture</span>
            </label>
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="ethanol"
                checked={services.ethanol}
                onChange={handleServiceChange}
              />
              <span>Reprogrammation moteur à l&apos;éthanol (E85)</span>
            </label>
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="diagnostic"
                checked={services.diagnostic}
                onChange={handleServiceChange}
              />
              <span>Diagnostic électronique complet</span>
            </label>
          </div>
        </div>
        <div className="hero-actions">
          <button
            type="button"
            className={`primary-button hero-cta ${
              showTour && tourStep === 2 && currentPage === 'accueil' ? 'tour-highlight' : ''
            }`}
            onClick={() => {
              setCurrentPage('devis');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            Demander un devis
          </button>
        </div>
      </section>

      <section className="info-grid">
        <article className="info-card clickable" onClick={() => setCurrentPage('cles')}>
          <h3>Reproduction de clés de voitures</h3>
          <p>
            Clés manuelles ou électroniques, nous reproduisons une clé identique à l&apos;originale avec un
            service rapide et soigné.
          </p>
        </article>
        <article className="info-card clickable" onClick={() => setCurrentPage('ethanol')}>
          <h3>Reprogrammation éthanol</h3>
          <p>
            Conversion E85, optimisation de la cartographie moteur et économies de carburant tout en
            préservant la longévité du moteur.
          </p>
        </article>
        <article className="info-card clickable" onClick={() => setCurrentPage('diagnostic')}>
          <h3>Diagnostic électronique</h3>
          <p>
            Lecture des codes défaut, détection de pannes et contrôle complet des systèmes électroniques
            de ton véhicule.
          </p>
        </article>
        <article className="info-card clickable" onClick={() => setCurrentPage('devis')}>
          <h3>Demande de devis rapide</h3>
          <p>
            Renseigne ton véhicule et tes besoins, reçois une estimation personnalisée et une réponse
            rapide de l&apos;atelier.
          </p>
        </article>
      </section>

      <section className="info-grid">
        <article className="info-card">
          <h3>L&apos;Univers Diag, votre atelier automobile de confiance</h3>
          <p>
            Mécaniciens professionnels, nous garantissons un service réactif et efficace pour répondre à
            tous vos besoins : reproduction de clés, reprogrammation éthanol, diagnostic électronique.
          </p>
          <p className="summary-title">Pourquoi choisir L&apos;Univers Diag ?</p>
          <ul>
            <li>Conseil</li>
            <li>Rapidité d&apos;intervention</li>
            <li>Travail de qualité</li>
            <li>Disponibilité</li>
          </ul>
        </article>
        <article className="info-card">
          <h3>Notre zone de déplacement</h3>
          <p>
            Nous nous déplaçons dans un rayon de 50 km autour de Toulouse : Balma, Labège, L&apos;Union,
            Villefranche-de-Lauragais, Auterive, Muret, Cazères, Carbonne, Saint-Gaudens, Blagnac,
            Colomiers, Aucamville, Bruguières, Fronton...
          </p>
        </article>
      </section>
    </>
  );

  const renderCles = () => (
    <>
      <section className="intro-card">
        <h2>Reproduction de clés de voitures à Toulouse</h2>
        <p>
          L&apos;Univers Diag à Toulouse est l&apos;adresse qu&apos;il te faut pour la reproduction de clés de
          voiture.
        </p>
        <p>
          Que tu possèdes des clés manuelles traditionnelles ou électroniques, nous reproduisons une
          clé identique à l&apos;originale.
        </p>
        <p>
          Qualifiés pour manipuler toutes sortes de clés et de systèmes d&apos;immobilisation, nous
          garantissons un travail soigné ainsi qu&apos;un service d&apos;intervention en urgence et de dépannage
          rapide.
        </p>
      </section>
    </>
  );

  const renderEthanol = () => (
    <>
      <section className="intro-card">
        <h2>Reprogrammation du moteur à l&apos;éthanol (E85)</h2>
        <p>
          Améliore les performances de ton moteur avec L&apos;Univers Diag à Toulouse. Chaque véhicule
          bénéficie d&apos;une optimisation adaptée à ses besoins.
        </p>
        <p>
          Pour la conversion à l&apos;éthanol, deux solutions s&apos;offrent à toi : installation d&apos;un boîtier
          éthanol (kit E85) ou reprogrammation directe de la cartographie moteur.
        </p>
        <p>
          La reprogrammation à l&apos;éthanol permet un gain économique important : jusqu&apos;à 2 000 €
          d&apos;économies par an pour un conducteur parcourant 15 à 20 000 km.
        </p>
        <p>
          Tarifs éthanol : sur devis, en fonction du véhicule et de la solution choisie.
        </p>
      </section>
    </>
  );

  const renderDiagnostic = () => (
    <>
      <section className="intro-card">
        <h2>Diagnostic électronique à Toulouse</h2>
        <p>
          L&apos;Univers Diag est le partenaire qu&apos;il te faut pour le diagnostic électronique de ton
          automobile.
        </p>
        <p>
          Le diagnostic permet d&apos;identifier les pannes et problèmes : voyant allumé, panne électrique,
          perte de puissance, problème de démarrage, etc.
        </p>
        <p>
          Nous lisons les codes d&apos;erreur du moteur, de la transmission, du système de freinage, de la
          climatisation et des systèmes de sécurité, puis nous proposons les réparations adaptées.
        </p>
      </section>
    </>
  );

  const renderDevis = () => (
    <>
      <section className="intro-card">
        <h2>Demande de devis personnalisé</h2>
        <p>
          Renseigne ton véhicule, choisis tes prestations et laisse tes coordonnées pour recevoir une
          estimation indicative. L&apos;atelier te recontactera pour confirmer le devis.
        </p>
      </section>

      <form className="grid-layout" onSubmit={handleSubmit}>
        <section className="card">
          <h3>1. Informations véhicule</h3>
          <div className="form-row">
            <label>
              Immatriculation (FR)
              <input
                type="text"
                name="plate"
                value={vehicle.plate}
                onChange={handleVehicleChange}
                placeholder="AB-123-CD"
                className="plate-input"
              />
            </label>
          </div>
          <div className="form-row grid-2">
            <label>
              Marque
              <input
                type="text"
                name="brand"
                value={vehicle.brand}
                onChange={handleVehicleChange}
                placeholder="Peugeot, BMW, Renault..."
              />
            </label>
            <label>
              Modèle
              <input
                type="text"
                name="model"
                value={vehicle.model}
                onChange={handleVehicleChange}
                placeholder="208, Clio, A3..."
              />
            </label>
          </div>
          <div className="form-row grid-2">
            <label>
              Année
              <input
                type="number"
                name="year"
                value={vehicle.year}
                onChange={handleVehicleChange}
                placeholder="2018"
                min="1990"
                max={new Date().getFullYear()}
              />
            </label>
            <label>
              Carburant
              <select name="fuel" value={vehicle.fuel} onChange={handleVehicleChange}>
                <option value="essence">Essence</option>
                <option value="diesel">Diesel</option>
                <option value="hybride">Hybride</option>
                <option value="electrique">Électrique</option>
              </select>
            </label>
          </div>
        </section>

        <section className="card">
          <h3>2. Prestations souhaitées</h3>
          <div className="checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="keyCopy"
                checked={services.keyCopy}
                onChange={handleServiceChange}
              />
              <span>Reproduction de clé de voiture</span>
            </label>
            <p className="hint">Clés manuelles ou électroniques, toutes marques.</p>

            <label className="checkbox-item">
              <input
                type="checkbox"
                name="ethanol"
                checked={services.ethanol}
                onChange={handleServiceChange}
              />
              <span>Reprogrammation moteur à l&apos;éthanol (E85)</span>
            </label>
            <p className="hint">
              Optimisation de la cartographie moteur pour rouler au superéthanol E85 et réaliser des
              économies.
            </p>

            <label className="checkbox-item">
              <input
                type="checkbox"
                name="diagnostic"
                checked={services.diagnostic}
                onChange={handleServiceChange}
              />
              <span>Diagnostic électronique complet</span>
            </label>
            <p className="hint">
              Lecture des codes défaut, détection de pannes, contrôle des systèmes électroniques.
            </p>

            <label className="checkbox-item urgent">
              <input
                type="checkbox"
                name="urgent"
                checked={services.urgent}
                onChange={handleServiceChange}
              />
              <span>Intervention urgente (sous réserve de disponibilité)</span>
            </label>
          </div>
        </section>

        <section className="card">
          <h3>3. Vos coordonnées</h3>
          <div className="form-row grid-2">
            <label>
              Prénom
              <input
                type="text"
                name="firstName"
                value={customer.firstName}
                onChange={handleCustomerChange}
              />
            </label>
            <label>
              Nom
              <input
                type="text"
                name="lastName"
                value={customer.lastName}
                onChange={handleCustomerChange}
              />
            </label>
          </div>
          <div className="form-row grid-2">
            <label>
              Téléphone
              <input
                type="tel"
                name="phone"
                value={customer.phone}
                onChange={handleCustomerChange}
                placeholder="06..."
              />
            </label>
            <label>
              E-mail
              <input
                type="email"
                name="email"
                value={customer.email}
                onChange={handleCustomerChange}
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              Ville / zone
              <input
                type="text"
                name="city"
                value={customer.city}
                onChange={handleCustomerChange}
                placeholder="Toulouse, Blagnac, Muret..."
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              Détail de la demande
              <textarea
                name="notes"
                value={customer.notes}
                onChange={handleCustomerChange}
                rows={4}
                placeholder="Précise le problème, la situation (perte totale de clés, voyant moteur allumé, conversion E85, etc.)."
              />
            </label>
          </div>
        </section>

        <section className="card summary-card">
          <h3>4. Estimation &amp; récapitulatif</h3>
          <div className="summary-vehicle">
            <p className="summary-title">Véhicule</p>
            <p>
              {vehicle.brand || vehicle.model || vehicle.plate
                ? `${vehicle.brand || 'Marque ?'} ${vehicle.model || ''} (${vehicle.year || 'Année ?'})`
                : 'Renseigne ton véhicule pour une estimation plus précise.'}
            </p>
            {vehicle.plate && <p>Immatriculation : {vehicle.plate}</p>}
          </div>

          <div className="summary-prices">
            {priceDetails.lines.length === 0 ? (
              <p className="hint">Sélectionne au moins une prestation pour voir l&apos;estimation.</p>
            ) : (
              <>
                <ul>
                  {priceDetails.lines.map((line) => (
                    <li key={line.label}>
                      <span>{line.label}</span>
                      <span className="price">{line.price} €</span>
                    </li>
                  ))}
                </ul>
                <div className="summary-total">
                  <span>Total estimatif</span>
                  <span className="price-large">{priceDetails.total} €</span>
                </div>
                <p className="disclaimer">
                  Ces tarifs sont donnés à titre indicatif et peuvent varier selon l&apos;état du véhicule, le
                  type de clé ou les spécificités techniques. Un devis précis te sera confirmé par
                  L&apos;Univers Diag.
                </p>
              </>
            )}
          </div>

          <button type="submit" className="primary-button">
            Envoyer ma demande à L&apos;Univers Diag
          </button>
        </section>
      </form>
    </>
  );

  const renderContact = () => (
    <>
      <section className="intro-card">
        <h2>Contactez L&apos;Univers Diag à Toulouse</h2>
        <p>Pose-nous tes questions ou demande un rappel, nous te répondrons rapidement.</p>
        <p>
          Nos horaires : du lundi au vendredi de 10h à 19h, le samedi de 10h à 16h. Nous sommes
          disponibles pour la reproduction de clés, la reprogrammation éthanol et le diagnostic
          électronique.
        </p>
      </section>

      <section className="card">
        <h3>Formulaire de contact</h3>
        <div className="form-row grid-2">
          <label>
            Nom / Prénom
            <input type="text" placeholder="Ton nom" />
          </label>
          <label>
            E-mail
            <input type="email" placeholder="ton@email.fr" />
          </label>
        </div>
        <div className="form-row grid-2">
          <label>
            Téléphone
            <input type="tel" placeholder="07 58 04 43 77" />
          </label>
          <label>
            Sujet
            <input type="text" placeholder="Reproduction de clé, Éthanol, Diagnostic..." />
          </label>
        </div>
        <div className="form-row">
          <label>
            Message
            <textarea rows={4} placeholder="Explique ta demande ou ton problème."></textarea>
          </label>
        </div>
        <p className="footer-small">
          Les informations de ce formulaire sont utilisées uniquement pour répondre à ta demande. Elles ne
          sont pas partagées avec des tiers.
        </p>
      </section>
    </>
  );

  return (
    <div className={`app-root ${theme}`}>
      {!unlocked ? (
        <div className="coming-soon">
          <div className="coming-card">
            <h1>L'Univers Diag</h1>
            <p className="coming-sub">Site en construction</p>
            <p className="coming-text">
              Notre site arrive très bientôt. En attendant, appelle-nous pour obtenir ton devis ou une
              intervention rapide.
            </p>
            <a className="coming-call" href="tel:0758044377">Appeler le 07 58 04 43 77</a>

            <div className="coming-sep" />
            <form
              className="coming-unlock"
              onSubmit={(e) => {
                e.preventDefault();
                if (unlockInput.trim() === PREVIEW_PASSWORD) {
                  setUnlocked(true);
                  setUnlockError('');
                  try { window.localStorage.setItem('universdiag_site_unlocked', '1'); } catch (e) {}
                } else {
                  setUnlockError('Mot de passe incorrect.');
                }
              }}
            >
              <label>
                Accès prestataire (mot de passe)
                <input
                  type="password"
                  value={unlockInput}
                  onChange={(e) => setUnlockInput(e.target.value)}
                  placeholder="Mot de passe"
                />
              </label>
              <button type="submit" className="coming-unlock-btn">Accéder au site en construction</button>
              {unlockError && <p className="coming-error">{unlockError}</p>}
            </form>
          </div>
          <footer className="coming-footer">© {new Date().getFullYear()} L'Univers Diag – Toulouse</footer>
        </div>
      ) : (
        <>
          <header className="app-header-bar">
            <div className="app-header-content">
              <div>
                <h1>L&apos;Univers Diag</h1>
                <p className="subtitle">Atelier de reprogrammation automobile à Toulouse</p>
              </div>
              <div className="header-right">
                <button
                  type="button"
                  className={`theme-toggle ${
                    showTour && tourStep === 3 && currentPage === 'accueil' ? 'tour-highlight' : ''
                  }`}
                  onClick={toggleTheme}
                  aria-label="Changer le thème clair / sombre"
                >
                  <span className="theme-icon" />
                </button>
                <div className="contact-short">
                  <p className="contact-phone">NOUS APPELER</p>
                  <p className="contact-phone-number">07 58 04 43 77</p>
                  <p className="contact-hours">Lun–Ven : 10h–19h • Sam : 10h–16h</p>
                </div>
                <button
                  type="button"
                  className="nav-toggle"
                  onClick={() => setNavOpen((open) => !open)}
                  aria-label="Ouvrir le menu de navigation"
                >
                  <span className="nav-toggle-bar" />
                  <span className="nav-toggle-bar" />
                  <span className="nav-toggle-bar" />
                </button>
              </div>
            </div>
          </header>

          {navOpen && <div className="nav-overlay" onClick={() => setNavOpen(false)} />}

          <nav className={`main-nav ${navOpen ? 'open' : ''}`}>
            <ul className="nav-list">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${currentPage === 'accueil' ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentPage('accueil');
                    setNavOpen(false);
                  }}
                >
                  Accueil
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${currentPage === 'cles' ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentPage('cles');
                    setNavOpen(false);
                  }}
                >
                  Reproduction de clés
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${currentPage === 'ethanol' ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentPage('ethanol');
                    setNavOpen(false);
                  }}
                >
                  Reprogrammation éthanol
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${currentPage === 'diagnostic' ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentPage('diagnostic');
                    setNavOpen(false);
                  }}
                >
                  Diagnostic électronique
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${currentPage === 'devis' ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentPage('devis');
                    setNavOpen(false);
                  }}
                >
                  Demande de devis
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${currentPage === 'contact' ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentPage('contact');
                    setNavOpen(false);
                  }}
                >
                  Contact
                </button>
              </li>
            </ul>
          </nav>

          <main className="app-main">
            {currentPage === 'accueil' && renderAccueil()}
            {currentPage === 'cles' && renderCles()}
            {currentPage === 'ethanol' && renderEthanol()}
            {currentPage === 'diagnostic' && renderDiagnostic()}
            {currentPage === 'devis' && renderDevis()}
            {currentPage === 'contact' && renderContact()}
          </main>

          <footer className="app-footer">
            <p>
              © {new Date().getFullYear()} L&apos;Univers Diag – Atelier de reprogrammation automobile à Toulouse.
            </p>
            <p className="footer-small">
              Les informations collectées via ce site sont utilisées uniquement pour répondre à ta demande. Tu
              disposes d&apos;un droit d&apos;accès, de rectification et d&apos;opposition conformément à la réglementation
              en vigueur.
            </p>
            <button
              type="button"
              className="tour-restart"
              onClick={() => {
                setCurrentPage('accueil');
                setShowTour(true);
                setTourStep(0);
                try {
                  window.localStorage.removeItem('universdiag_tour_done');
                } catch (e) {
                  // ignore
                }
              }}
            >
              Revoir le guide d&apos;utilisation
            </button>
          </footer>

          {showThemeModal && (
            <div className="theme-modal-backdrop">
              <div className="theme-modal">
                <h2>Choisis ton thème</h2>
                <p>Tu préfères une interface claire ou sombre pour L&apos;Univers Diag&nbsp;?</p>
                <div className="theme-modal-actions">
                  <button
                    type="button"
                    className="theme-choice light"
                    onClick={() => {
                      setTheme('light');
                      setSelectedTheme('light');
                    }}
                  >
                    Thème clair
                  </button>
                  <button
                    type="button"
                    className="theme-choice dark"
                    onClick={() => {
                      setTheme('dark');
                      setSelectedTheme('dark');
                    }}
                  >
                    Thème sombre
                  </button>
                </div>
                <div className="theme-modal-footer">
                  <button
                    type="button"
                    className="theme-modal-skip"
                    onClick={() => {
                      setShowThemeModal(false);
                    }}
                  >
                    Décider plus tard
                  </button>
                  <button
                    type="button"
                    className="theme-modal-ok"
                    onClick={() => {
                      try {
                        window.localStorage.setItem('universdiag_theme', selectedTheme);
                        const tourDone = window.localStorage.getItem('universdiag_tour_done') === '1';
                        if (!tourDone) {
                          setShowTour(true);
                          setTourStep(0);
                        }
                      } catch (e) {
                        // ignore
                      }
                      setShowThemeModal(false);
                    }}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}

          {showTour && currentPage === 'accueil' && !showThemeModal && (
            <div className="tour-overlay">
              <div
                className={`tour-tooltip ${tourStep === 0 ? 'step-plate' : ''} ${
                  tourStep === 1 ? 'step-services' : ''
                } ${tourStep === 2 ? 'step-cta' : ''} ${tourStep === 3 ? 'step-theme' : ''}`}
              >
                {tourStep === 0 && (
                  <>
                    <p className="tour-title">Étape 1 : ta plaque</p>
                    <p>Saisis ici la plaque d&apos;immatriculation de ton véhicule. C&apos;est la base de ta demande.</p>
                  </>
                )}
                {tourStep === 1 && (
                  <>
                    <p className="tour-title">Étape 2 : prestations</p>
                    <p>Coche une ou plusieurs prestations : clé, reprogrammation éthanol, diagnostic électronique.</p>
                  </>
                )}
                {tourStep === 2 && (
                  <>
                    <p className="tour-title">Étape 3 : demander un devis</p>
                    <p>Clique sur &quot;Demander un devis&quot; pour aller au formulaire avec les infos déjà pré-remplies.</p>
                  </>
                )}
                {tourStep === 3 && (
                  <>
                    <p className="tour-title">Étape 4 : changer de thème</p>
                    <p>
                      Ici tu peux passer du thème sombre au thème clair (et inversement) à tout moment pendant ta
                      navigation.
                    </p>
                  </>
                )}
                <div className="tour-footer">
                  <button
                    type="button"
                    className="tour-skip"
                    onClick={() => {
                      setShowTour(false);
                      try {
                        window.localStorage.setItem('universdiag_tour_done', '1');
                      } catch (e) {
                        // ignore
                      }
                    }}
                  >
                    Ignorer
                  </button>
                  {tourStep < 3 ? (
                    <button
                      type="button"
                      className="tour-next"
                      onClick={() => setTourStep((step) => Math.min(step + 1, 3))}
                    >
                      Suivant
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="tour-next"
                      onClick={() => {
                        setShowTour(false);
                        try {
                          window.localStorage.setItem('universdiag_tour_done', '1');
                        } catch (e) {
                          // ignore
                        }
                      }}
                    >
                      Terminer
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default App;
